import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Errors } from '@ai4/data-request';
import { ErrorElement, ErrorAlert, SuccessAlert, FormViewerProps } from '@ai4/form-viewer';
import Table, { Props as TableProps } from '../Table/Table';
import EditPopup, { Props as EditPopupProps } from '../EditPopup/EditPopup';
import { EditOptions, EditSlots, TableSlots } from 'src/types/general';
import { getHash } from 'src/helpers';

interface OnBootArgs {
	newRecord: (values: any) => void;
}

interface Props<Item> extends Omit<EditPopupProps, 'slots'>, Omit<TableProps<Item>, 'slots'> {
	onBoot?: (args: OnBootArgs) => void;
	entity: string;
	tableClassName?: string;
	options?: EditOptions;
	slots?: TableSlots<Item> & EditSlots & FormViewerProps['slots'];
}

export function RecordsManagement<Item = any>(props: Props<Item>) {
	const {
		entity,
		tableClassName,
		onBoot,
		onSubmit,
		onCancel,
		onSelectedRow,
		onDeleteRow,
		options = {},
		slots,
		rows,
		record,
	} = props;
	const [selectedRow, setSelectedRow] = useState<Partial<Item>>();
	const [saveSuccess, setSaveSuccess] = useState();
	const [saveError, setSaveError] = useState<Errors>();
	const $booted = useRef<boolean>(false);

	useEffect(() => {
		if (rows && !$booted.current) {
			if (onBoot) {
				onBoot({
					newRecord,
				});
			}
			parseHash();
			$booted.current = true;
		}
	}, [rows]);

	useEffect(() => {
		setSelectedRow(record);
	}, [record]);

	const parseHash = useCallback(() => {
		let hash = getHash();
		if (hash) {
			if (typeof hash === 'string') {
				if (hash === 'create') {
					hash = [{ entity, action: 'create' }];
				} else if (hash.length === 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.length) {
					hash = [{ entity, action: 'edit', uniqueId: hash }];
				}
			}
			const found = (hash as any[]).find((h) => h.entity === entity);
			if (found) {
				const { action, uniqueId } = found;
				// don't know why we must wait a tick
				setTimeout(() => {
					if (action === 'create') {
						newRecord(options && options.onCreateInitialValues ? { ...options.onCreateInitialValues } : {});
					}
					if (action === 'edit') {
						const edit = (
							rows ? rows.find((r: any) => `${r.uniqueId}` === `${uniqueId}`) : { uniqueId }
						) as Item;
						if (edit) {
							onSelectedRow ? onSelectedRow(edit) : newRecord(edit);
						}
					}
				}, 0);
			}
		}
	}, [rows, onSelectedRow]);

	// when rows are updated update selectedRow too
	/*
	// 2023-01-09: deprecated cause now is graphql driven
	useEffect(() => {
		if (selectedRow) {
			const found = rows.find((row) => (row as any).uniqueId === (selectedRow as any).uniqueId);
			if (found) {
				setSelectedRow(found);
			}
		}
	}, [rows]);
	*/

	const newRecord = useCallback((initialValues = {}) => {
		setSelectedRow(initialValues);
	}, []);

	const listProps = {
		onSelectedRow: (row: Item) => {
			if (onSelectedRow) {
				onSelectedRow(row);
			} else {
				setSelectedRow(row);
			}
		},
		onDeleteRow: (row: Item) => {
			if (onDeleteRow) {
				onDeleteRow(row);
			}
		},
	};

	const detail = selectedRow ? (
		<EditPopup
			{...props}
			record={selectedRow}
			onSubmit={onSubmit}
			onCancel={() => {
				setSelectedRow(undefined);
				onCancel();
			}}
			onSuccess={(values) => {
				setSaveError(undefined);
				setSaveSuccess(values);
				if (options.closeWhenSaved) {
					setSelectedRow(undefined);
				} else {
					listProps.onSelectedRow(values);
					/*
					// refresh only in update
					if (!(selectedRow as any).uniqueId) {
						listProps.onSelectedRow(values);
					}
					*/
				}
			}}
			onError={(err) => {
				setSaveError(err.message);
			}}
		/>
	) : null;

	return (
		<div>
			{saveError && !options.hideErrors && (
				<ErrorAlert errors={saveError as ErrorElement} onClose={() => setSaveError(undefined)} />
			)}
			{saveSuccess && (
				<SuccessAlert text='Contenuto salvato correttamente' onClose={() => setSaveSuccess(undefined)} />
			)}
			<div style={{ display: selectedRow && slots && slots.edit && !options.forceModal ? 'none' : 'block' }}>
				{slots && slots.header && slots.header({ onCreate: newRecord })}
				{(!slots || !slots.header) && (
					<Button variant='secondary' onClick={() => newRecord()}>
						Aggiungi nuovo
					</Button>
				)}
				<div>
					{slots && slots.list && slots.list({ ...props, ...listProps })}
					{(!slots || !slots.list) && (
						<Table<Item> {...props} {...listProps} className={tableClassName || ''} />
					)}
				</div>
			</div>
			{detail}
		</div>
	);
}

export default RecordsManagement;
